import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { UserSurveyService } from '../../services/user-survey.service';
import { ToastrService } from 'ngx-toastr';
import { CountrySelectionService } from '../../services/country-selection.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface SurveyButton {
  caption: string;
  nid: string;
}

@Component({
  selector: 'app-user-survey',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './user-survey.component.html',
  styleUrls: ['./user-survey.component.css']
})
export class UserSurveyComponent implements OnInit, OnDestroy {
  buttons: SurveyButton[] = [];
  surveys: any[] = [];
  selectedSurvey: any = null;
  surveyForm!: FormGroup;
  private ngUnsubscribe = new Subject<void>();

  constructor(
    private userSurveyService: UserSurveyService,
    private toastr: ToastrService,
    private countrySelectionService: CountrySelectionService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.countrySelectionService.selectedCountry$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(country => {
        if (country) {
          this.resetSurvey();
          this.loadSurveyQuestions();
        }
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  resetSurvey() {
    this.surveys = [];
    this.buttons = [];
    this.selectedSurvey = null;
    if (this.surveyForm) {
      this.surveyForm.reset();
    }
  }

  loadSurveyQuestions() {
    this.userSurveyService.getSurveyQuestions().subscribe({
      next: (response) => {
        this.surveys = response;
        this.buttons = response.map((survey: any) => ({
          caption: survey.title,
          nid: survey.nid
        }));
      },
      error: (error) => {
        this.toastr.error('Failed to load survey questions. Please try again.');
        console.error('Load Survey Questions Error:', error);
      }
    });
  }

  onButtonClick(caption: string) {
    this.selectedSurvey = this.surveys.find(survey => survey.title === caption);
    const controls: { [key: string]: any } = {};
    this.selectedSurvey.field_srh_survey_question_export.forEach((question: any) => {
      if (question.survey_poll_question_type === 'Multiple Choice Question') {
        controls[question.id] = this.fb.array([], Validators.minLength(1));
      } else {
        controls[question.id] = [null, Validators.required];
      }
    });
    this.surveyForm = this.fb.group(controls);
  }

  onCheckboxChange(event: Event, questionId: string) {
    const checkbox = event.target as HTMLInputElement;
    const formArray = this.surveyForm.get(questionId) as FormArray;

    if (checkbox.checked) {
      formArray.push(new FormControl(checkbox.value));
    } else {
      const index = formArray.controls.findIndex(x => x.value === checkbox.value);
      formArray.removeAt(index);
    }
  }

  submitSurvey() {
    if (this.surveyForm.invalid) {
      this.toastr.error('Please answer all questions before submitting.');
      this.surveyForm.markAllAsTouched();
      return;
    }

    const country = this.countrySelectionService.getSelectedCountry();
    const surveyType = this.selectedSurvey.field_poll_or_survey === 'Poll' ? 'Poll Response' : 'Survey Response';

    const userResponses = this.selectedSurvey.field_srh_survey_question_export.map((question: any) => {
      const questionTitle = question.survey_question_title;
      const userAnswer = this.surveyForm.value[question.id];
      return `Question title : ${questionTitle}\nUser selected answer : ${userAnswer}`;
    }).join('\n\n');

    const payload = {
      title: { value: surveyType },
      field_survey_poll_reference: { value: this.selectedSurvey.nid },
      field_country: { value: country.id },
      field_user_responses: { value: userResponses }
    };

    this.userSurveyService.submitSurvey(payload).subscribe({
      next: (response) => {
        this.toastr.success('Survey submitted successfully!');
        this.selectedSurvey = null;
      },
      error: (error) => {
        this.toastr.error('Failed to submit survey. Please try again.');
        console.error('Submit Survey Error:', error);
      }
    });
  }
}